import { z } from 'zod';

// ============================================================================
// ENUM (moved here from vault.types.ts to prevent circular dependencies)
// ============================================================================
export enum ProtectionPaymentType { 
  OneTime = "OneTime",
  Monthly = "Monthly",
}

// ============================================================================
// CONSTANTS
// ============================================================================
const PROTECTION_PRICE_CONSTANTS = {
  MIN_VALUE: 0,
  MIN_INTEREST_RATE: 0,
  MAX_INTEREST_RATE: 100,
  MIN_ANNUAL_PAYMENT: 0,
  DATE_FORMAT_LENGTH: 10,
} as const;

// ============================================================================
// LENIENT SCHEMAS (for deserialization)
// ============================================================================
const lenientOneTimeSchema = z.object({
  mode: z.literal(ProtectionPaymentType.OneTime),
  value: z.number(),
  coverEndDate: z.string(),
}).transform(val => ({
  mode: val.mode,
  value: val.value,
  coverEndDate: val.coverEndDate,
}));

const lenientMonthlySchema = z.object({
  mode: z.literal(ProtectionPaymentType.Monthly),
  value: z.number(),
  coverEndDate: z.string(),
  interestRate: z.number(),
  annualPayment: z.number(),
}).transform(val => ({
  mode: val.mode,
  value: val.value,
  coverEndDate: val.coverEndDate,
  interestRate: val.interestRate,
  annualPayment: val.annualPayment,
}));

const lenientProtectionPriceNodeSchema = z.union([
  lenientOneTimeSchema,
  lenientMonthlySchema,
]);

// ============================================================================
// STRICT SCHEMAS (for validation)
// ============================================================================
const strictOneTimeSchema = z.object({
  mode: z.literal(ProtectionPaymentType.OneTime),
  value: z.number()
    .min(PROTECTION_PRICE_CONSTANTS.MIN_VALUE, `Value must be at least ${PROTECTION_PRICE_CONSTANTS.MIN_VALUE}`),
  coverEndDate: z.string()
    .length(PROTECTION_PRICE_CONSTANTS.DATE_FORMAT_LENGTH, `Cover end date must be in YYYY-MM-DD format`)
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Cover end date must be in YYYY-MM-DD format'),
}).transform(val => ({
  mode: val.mode,
  value: val.value,
  coverEndDate: val.coverEndDate,
}));

const strictMonthlySchema = z.object({
  mode: z.literal(ProtectionPaymentType.Monthly),
  value: z.number()
    .min(PROTECTION_PRICE_CONSTANTS.MIN_VALUE, `Value must be at least ${PROTECTION_PRICE_CONSTANTS.MIN_VALUE}`),
  coverEndDate: z.string()
    .length(PROTECTION_PRICE_CONSTANTS.DATE_FORMAT_LENGTH, `Cover end date must be in YYYY-MM-DD format`)
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Cover end date must be in YYYY-MM-DD format'),
  interestRate: z.number()
    .min(PROTECTION_PRICE_CONSTANTS.MIN_INTEREST_RATE, `Interest rate must be at least ${PROTECTION_PRICE_CONSTANTS.MIN_INTEREST_RATE}`)
    .max(PROTECTION_PRICE_CONSTANTS.MAX_INTEREST_RATE, `Interest rate must be at most ${PROTECTION_PRICE_CONSTANTS.MAX_INTEREST_RATE}`),
  annualPayment: z.number()
    .min(PROTECTION_PRICE_CONSTANTS.MIN_ANNUAL_PAYMENT, `Annual payment must be at least ${PROTECTION_PRICE_CONSTANTS.MIN_ANNUAL_PAYMENT}`),
}).transform(val => ({
  mode: val.mode,
  value: val.value,
  coverEndDate: val.coverEndDate,
  interestRate: val.interestRate,
  annualPayment: val.annualPayment,
}));

const strictProtectionPriceNodeSchema = z.union([
  strictOneTimeSchema,
  strictMonthlySchema,
]);

// ============================================================================
// TYPE
// ============================================================================
export type ProtectionPriceNode = z.infer<typeof lenientProtectionPriceNodeSchema>;

// ============================================================================
// NAMESPACE (for Constants and Methods)
// ============================================================================
export namespace ProtectionPriceNode {
  export const Constants = PROTECTION_PRICE_CONSTANTS;

  export function deserialize(data: unknown): ProtectionPriceNode {
    return lenientProtectionPriceNodeSchema.parse(data);
  }

  export function create(data: unknown): ProtectionPriceNode {
    return strictProtectionPriceNodeSchema.parse(data);
  }
}
